import { 
  FileText, 
  FileSpreadsheet, 
  Image as ImageIcon, 
  FileCode, 
  Archive, 
  Film, 
  Music, 
  FileQuestion
} from "lucide-react"

export interface FileIconProps {
  fileName: string
  fileType?: string
  contentType?: string
  className?: string
  size?: "sm" | "md" | "lg" | "xl"
}

export function getFileCategory(fileName: string, fileType?: string, contentType?: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  
  if (fileType === 'image' || contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return 'image'
  }
  if (['pdf'].includes(ext) || fileType === 'pdf' || contentType === 'application/pdf') {
    return 'pdf'
  }
  if (['xls', 'xlsx', 'csv', 'sheet'].includes(ext) || fileType === 'sheet' || contentType?.includes('spreadsheet') || contentType?.includes('excel')) {
    return 'sheet'
  }
  if (['doc', 'docx'].includes(ext) || fileType === 'document' || contentType?.includes('word')) {
    return 'document'
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext) || fileType === 'archive' || contentType?.includes('zip') || contentType?.includes('tar')) {
    return 'archive'
  }
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext) || fileType === 'video' || contentType?.startsWith('video/')) {
    return 'video'
  }
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(ext) || fileType === 'audio' || contentType?.startsWith('audio/')) {
    return 'audio'
  }
  if (['json', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'py', 'go', 'md', 'txt', 'log'].includes(ext)) {
    return 'code'
  }
  return 'other'
}

export function FileIcon({ fileName, fileType, contentType, className = "", size = "md" }: FileIconProps) {
  const category = getFileCategory(fileName, fileType, contentType)

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-8 h-8",
    xl: "w-12 h-12",
  }[size]

  switch (category) {
    case 'image':
      return <ImageIcon className={`text-sky-500 ${sizeClasses} ${className}`} />
    case 'pdf':
      return <FileText className={`text-rose-500 ${sizeClasses} ${className}`} />
    case 'sheet':
      return <FileSpreadsheet className={`text-emerald-500 ${sizeClasses} ${className}`} />
    case 'document':
      return <FileText className={`text-blue-500 ${sizeClasses} ${className}`} />
    case 'archive':
      return <Archive className={`text-amber-500 ${sizeClasses} ${className}`} />
    case 'video':
      return <Film className={`text-purple-500 ${sizeClasses} ${className}`} />
    case 'audio':
      return <Music className={`text-pink-500 ${sizeClasses} ${className}`} />
    case 'code':
      return <FileCode className={`text-emerald-600 ${sizeClasses} ${className}`} />
    default:
      return <FileQuestion className={`text-gray-400 ${sizeClasses} ${className}`} />
  }
}
